import * as React from 'react';
import * as _ from 'lodash';
import * as hash from 'object-hash';
import { IApplicationState, AppActionCreators, ContactActionCreators, IContactState, CenterActionCreators, IContactPositionListResponse, IContactResponse, IContactLookupDataResponse, IBasicCenterListResponse } from '@nkr/store/index';
import { IAppState } from '@nkr/store/application/state';
import { Dispatch, connect } from 'react-redux';
import { ICenter, IPhoneNumber, IEmailFlagValue, IContactRequest, ITag, ILogisticsProvider, ICenterBase, IPhoneNumberForEdit, IUser, IContactOverridePrimaryAddress } from '@nkr/models/index';
import { CenterContactPosition, ContactEntityType, NkrContactPosition, OrganType } from '@nkr/enums/index';
import { Panel, Row, Col, Button, Alert, Label, Modal } from 'react-bootstrap';
import { IOverrideAddress } from './override-address';
import { IValidation } from './validation';
import PhoneNumbersPanel from './phone-numbers';
import EmailOptions from './email-options';
import Gala from './gala';
import ConferenceCallPanel from './conference-call-panel';
import ValidationInfo from './validation-info';
import ContactInfoContent from './contact-info-content';
import EmailSignature from './email-signature';
import NetworkEmailSignature from './network-email-signature';

interface IProps {
    contactId: number,
    centerId: number,
    isCenter: boolean
}

interface IStateProps {
    contact: IContactState,
    centerList: ICenter[],
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getContact: (contactId: number) => Promise<IContactResponse>,
    getBasicCenterList: () => Promise<IBasicCenterListResponse>,
    getContactPositionList: () => Promise<IContactPositionListResponse>,
    getContactLookupData: (centerId: number, organType: number) => Promise<IContactLookupDataResponse>,
    saveContact: (request: IContactRequest) => Promise<IContactResponse>
}

type TProps = IStateProps & IDispatchProps & IProps

interface IState {
    id: number,
    organType: number,
    entityType: number,
    entityTypeId: number,
    firstName: string,
    middleName: string,
    lastName: string,
    title: string,
    prefix: string,
    suffix: string,
    email: string,
    secondaryEmail: string,
    emailSignature: string,
    emailOverride: string,
    conferenceNumber: string,
    conferenceNumberCode: string,
    conferenceNumberMobileFriendly: string,
    primaryAddress: number,
    primarySuite: string,
    primaryAddressOverride: IOverrideAddress,
    secondaryAddress: number,
    secondarySuite: string,
    secondaryAddressOverride: string,
    positions: number[],
    savedPositions: number[],
    emailFlags: IEmailFlagValue[],
    phoneNumbers: IPhoneNumberForEdit[],
    tags: ITag[],
    tagSuggestions: ITag[],
    center: ICenter,
    courierList: ILogisticsProvider[],
    centerList: ICenterBase[],
    user?: IUser,
    validation: IValidation,
    dataHash: string,
    pendingChanges: boolean,
    dirty: boolean,
    save: {
        status: number,
        message: string
    },
    showRemoveOpsModal?: boolean,
    pendingOpsSave?: number[] | null,
}

class AddEdit extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        organType: -1,
        entityType: 0,
        entityTypeId: 0,
        firstName: '',
        middleName: '',
        lastName: '',
        title: '',
        prefix: '',
        suffix: '',
        email: '',
        secondaryEmail: '',
        emailSignature: '',
        emailOverride: '',
        conferenceNumber: '',
        conferenceNumberCode: '',
        conferenceNumberMobileFriendly: '',
        primaryAddress: 0,
        primarySuite: '',
        primaryAddressOverride: {
            street: '',
            city: '',
            state: '',
            postalCode: ''
        },
        secondaryAddress: 0,
        secondarySuite: '',
        secondaryAddressOverride: '',
        positions: [],
        savedPositions: [],
        emailFlags: [],
        phoneNumbers: [],
        tags: [],
        tagSuggestions: [{ id: -1, name: 'VIP' }, { id: -2, name: 'Speaker' }],
        center: null,
        courierList: [],
        centerList: [],
        validation: {
            entityType: true,
            positions: true,
            firstName: true,
            lastName: true,
            email: true,
            primaryAddress: true,
            primaryAddressOverride: {
                street: true,
                city: true,
                state: true,
                postalCode: true
            },
            phoneNumbers: true,
            conferenceNumber: true,
            conferenceNumberCode: true,
            conferenceNumberMobileFriendly: true
        },
        dataHash: '',
        pendingChanges: false,
        dirty: false,
        save: {
            status: 0,
            message: ''
        },
    }

    componentDidMount = () => {
        this.getData();
    }

    isNetworkEmailSender = (): boolean => {
        return this.props.isCenter == false
            && this.state.entityType == ContactEntityType.Nkr
            && this.state.savedPositions.includes(NkrContactPosition.EmailSender);
    }

    requiresConferenceCall = (positions: number[]): boolean => {
        if (this.state.entityType !== ContactEntityType.Nkr) {
            return false;
        }

        return positions.includes(NkrContactPosition.Operations)
            || positions.includes(NkrContactPosition.SwapManager)
            || positions.includes(NkrContactPosition.DutyOfficer);
    }

    validate = () => {
        let { entityType, positions, firstName, lastName, email, primaryAddress, primaryAddressOverride, phoneNumbers } = this.state;
        let { street, city, state, postalCode } = primaryAddressOverride;

        let streetValid = true;
        let cityValid = true;
        let stateValid = true;
        let postalCodeValid = true;

        if (entityType != ContactEntityType.Center) {
            if (city != '' || state != '' || postalCode != '') {
                streetValid = street != '';
            }

            if (street != '' || state != '' || postalCode != '') {
                cityValid = city != '';
            }

            if (street != '' || city != '' || postalCode != '') {
                stateValid = state != '';
            }

            if (street != '' || city != '' || state != '') {
                postalCodeValid = postalCode != '';
            }
        }

        const requiresConferenceCall = this.requiresConferenceCall(positions);

        let validation: IValidation = {
            entityType: entityType > 0,
            positions: entityType == ContactEntityType.Center || entityType == ContactEntityType.Opo ? positions.length > 0 : true,
            firstName: firstName != '',
            lastName: lastName != '',
            email: email != '',
            primaryAddress: primaryAddress > 0,
            primaryAddressOverride: {
                street: streetValid,
                city: cityValid,
                state: stateValid,
                postalCode: postalCodeValid,
            },
            phoneNumbers: requiresConferenceCall ? (phoneNumbers.length > 0 ? phoneNumbers.filter(x => !x.isDeleted).length > 0 : false) : true,
            conferenceNumber: requiresConferenceCall ? this.state.conferenceNumber != '' : true,
            conferenceNumberCode: requiresConferenceCall ? this.state.conferenceNumberCode != '' : true,
            conferenceNumberMobileFriendly: requiresConferenceCall ? this.state.conferenceNumberMobileFriendly != '' : true
        };

        return validation;
    }

    getData = () => {
        this.props.showLoading('Getting contact, one moment please...').then(() => {
            Promise.all([
                this.props.getContact(this.props.contactId),
                this.props.getContactLookupData(this.props.centerId, this.props.app.site.organType),
                this.props.getBasicCenterList()
            ])
                .then(([contactResponse, lookupDataResponse, basicCenterListResponse]) => {
                    this.props.hideLoading();

                    if (lookupDataResponse.data.success) {
                        let swapCouriers = lookupDataResponse.data.results.swapCouriers;
                        this.setState({ courierList: swapCouriers });
                    }

                    if (contactResponse.data.success && contactResponse.data.results.contact != null) {
                        let { contact, allTags } = contactResponse.data.results;

                        let {
                            id, organType, entityType, entityTypeId, firstName, middleName, lastName, positionText: title,
                            nameHonorific: prefix, nameSuffix: suffix,
                            email, secondaryEmail, emailSignature,
                            addressId1: primaryAddress, address1Suite: primarySuite, address1Override: primaryAddressOverride,
                            addressId2: secondaryAddress, address2Suite: secondarySuite, address2Override: secondaryAddressOverride,
                            galaEmailOverride: emailOverride,
                            conferenceNumber, conferenceNumberCode, conferenceNumberMobileFriendly,
                            positions: positionList, emailFlags, phoneNumbers, tags, center, user
                        } = contact;

                        var positions = positionList.map(p => p.id);

                        emailFlags.forEach(flag => {
                            if (flag.value == 2) {
                                flag.value = 1;
                            }
                        });

                        let dataHash = this.calcHash(entityType, entityTypeId, title, prefix, firstName, middleName, lastName, suffix, email, secondaryEmail,
                            primaryAddress, primarySuite, primaryAddressOverride,
                            secondaryAddress, secondarySuite, secondaryAddressOverride, positions, phoneNumbers, emailFlags);

                        this.setState({
                            id,
                            organType,
                            entityType,
                            entityTypeId,
                            firstName, middleName, lastName,
                            title,
                            prefix,
                            suffix,
                            email,
                            secondaryEmail,
                            emailSignature,
                            primaryAddress,
                            primarySuite,
                            primaryAddressOverride,
                            secondaryAddress,
                            secondarySuite,
                            secondaryAddressOverride,
                            emailOverride,
                            conferenceNumber: conferenceNumber || '',
                            conferenceNumberCode: conferenceNumberCode || '',
                            conferenceNumberMobileFriendly: conferenceNumberMobileFriendly || '',
                            positions,
                            savedPositions: positions,
                            emailFlags,
                            phoneNumbers: phoneNumbers.map(x => ({ ...x, edit: false })),
                            tags: tags.map((t, i) => { return { id: i, name: t }; }),
                            tagSuggestions: allTags.map((t, i) => { return { id: i, name: t }; }),
                            center,
                            centerList: basicCenterListResponse.data.results,
                            user,
                            dataHash
                        });
                    } else {
                        let entityType = this.props.isCenter ? ContactEntityType.Center : 0;
                        let entityTypeId = this.props.isCenter ? this.props.centerId : 0;
                        let emailFlags = this.props.contact.lookupData.emailFlags.map(x => { return { flag: x.id, value: this.props.app.site.organType == OrganType.Kidney ? 1 : 0 }; });
                        let primaryAddressOverride = { street: '', city: '', state: '', postalCode: '' };

                        let dataHash = this.calcHash(entityType, entityTypeId, '', '', '', '', '', '', '', '',
                            0, '', primaryAddressOverride,
                            0, '', '', [], [], emailFlags);

                        this.setState({
                            entityType,
                            entityTypeId,
                            emailFlags,
                            tagSuggestions: contactResponse.data.results.allTags.map((t, i) => { return { id: i, name: t }; }),
                            centerList: basicCenterListResponse.data.results,
                            dataHash,
                            organType: this.props.app.site.organType == OrganType.Liver ? OrganType.Liver : null
                        });
                    }
                });
        });
    }

    calcHash = (
        entityType: number, entityTypeId: number, title: string, prefix: string,
        firstName: string, middleName: string, lastName: string,
        suffix: string,
        email: string, secondaryEmail: string, primaryAddress: number, primarySuite: string, primaryAddressOverride: IContactOverridePrimaryAddress,
        secondaryAddress: number, secondarySuite: string, secondaryAddressOverride: string, positions: number[], phoneNumbers: IPhoneNumber[], emailFlags: IEmailFlagValue[]
    ) => {
        let data = {
            entityType,
            entityTypeId,
            title, prefix,
            firstName,
            middleName,
            lastName,
            suffix,
            email, secondaryEmail,
            primaryAddress, primarySuite, primaryAddressOverride, secondaryAddress, secondarySuite, secondaryAddressOverride,
            positions, phoneNumbers, emailFlags
        };

        let h = hash(data);

        return h;
    }

    getHashData = () => {
        let {
            organType, entityType, entityTypeId, title, prefix,
            firstName, middleName, lastName,
            suffix,
            email, secondaryEmail,
            primaryAddress, primarySuite, primaryAddressOverride,
            secondaryAddress, secondarySuite, secondaryAddressOverride,
            positions, phoneNumbers, emailFlags
        } = this.state;

        let data = {
            organType,
            entityType,
            entityTypeId,
            title, prefix,
            firstName, middleName, lastName,
            suffix,
            email, secondaryEmail,
            primaryAddress, primarySuite, primaryAddressOverride, secondaryAddress, secondarySuite, secondaryAddressOverride,
            positions, phoneNumbers, emailFlags
        };

        return data;
    }

    handleNumberValueChange = (property: string, value: number) => {
        let hashData = this.getHashData();

        if (property == 'organType') {
            hashData.organType = value;

            let dataHash = hash(hashData);

            this.setState({
                organType: value,
                pendingChanges: this.state.dataHash != dataHash
            });
        }

        if (property == 'entityType') {
            hashData.entityType = value;
            hashData.entityTypeId = 0;

            let dataHash = hash(hashData);

            this.setState({
                entityType: value,
                entityTypeId: 0,
                pendingChanges: this.state.dataHash != dataHash
            });
        }

        if (property == 'center') {
            this.setState({
                entityTypeId: value,
                pendingChanges: true
            });
        }

        if (property == 'courier') {
            hashData.entityTypeId = value;

            let dataHash = hash(hashData);

            this.setState({
                entityTypeId: value,
                pendingChanges: this.state.dataHash != dataHash
            });
        }

        if (property == 'primaryAddress') {
            hashData.primaryAddress = value;

            let dataHash = hash(hashData);

            this.setState({
                primaryAddress: value,
                pendingChanges: this.state.dataHash != dataHash
            });
        }

        if (property == 'secondaryAddress') {
            hashData.secondaryAddress = value;

            let dataHash = hash(hashData);

            this.setState({
                secondaryAddress: value,
                pendingChanges: this.state.dataHash != dataHash
            });
        }
    }

    handleTextChange = (property: string, value: string) => {
        let state = _.cloneDeep(this.state);

        if (property.indexOf('.') >= 0) {
            let parts = property.split('.');
            state[parts[0]][parts[1]] = value;
        } else {
            state[property] = value;
        }

        let hashData = this.getHashData();
        hashData[property] = value;

        let dataHash = hash(hashData);

        state.pendingChanges = this.state.dataHash != dataHash;

        this.setState(state);
    }

    handlePhoneNumbersHaveChanged = (phoneNumbers: IPhoneNumberForEdit[]) => {
        let hashData = this.getHashData();
        hashData.phoneNumbers = phoneNumbers;

        let dataHash = hash(hashData);

        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.phoneNumbers = phoneNumbers.filter(x => !x.isDeleted).length > 0;
        }

        this.setState({
            phoneNumbers,
            validation,
            pendingChanges: this.state.dataHash != dataHash
        });
    }

    handleEmailFlagsHaveChanged = (emailFlags: IEmailFlagValue[]) => {
        let hashData = this.getHashData();
        hashData.emailFlags = emailFlags;

        let dataHash = hash(hashData);

        this.setState({
            emailFlags,
            pendingChanges: this.state.dataHash != dataHash
        });
    }

    handleAddRoles = (list: number[]) => {
        let currentPositions = [...this.state.positions];
        let positions = currentPositions.concat(list);

        let hashData = this.getHashData();
        hashData.positions = positions;

        let dataHash = hash(hashData);

        this.setState({
            positions,
            pendingChanges: this.state.dataHash != dataHash
        });
    }

    handleRemoveRoles = (list: number[]) => {
        const OPERATIONS_ROLE_ID = 11;
        if (list.includes(OPERATIONS_ROLE_ID) && this.state.positions.includes(OPERATIONS_ROLE_ID) && this.state.id > 0) {
            this.setState({ showRemoveOpsModal: true, pendingOpsSave: list });
        } else {
            this._actuallyRemoveRoles(list);
        }
    }

    handleConfirmRemoveOps = () => {
        if (this.state.pendingOpsSave) {
            this._actuallyRemoveRoles(this.state.pendingOpsSave);
        }
    }

    handleCancelRemoveOps = () => {
        this.setState({
            showRemoveOpsModal: false,
            pendingOpsSave: null
        });
    }

    _actuallyRemoveRoles = (list: number[]) => {
        let positions = this.state.positions.filter(id => list.indexOf(id) == -1);

        const removedConferenceCallRoles = list.filter(id => [8, 11, 15].includes(id));

        let conferenceNumber = this.state.conferenceNumber;
        let conferenceNumberCode = this.state.conferenceNumberCode;
        let conferenceNumberMobileFriendly = this.state.conferenceNumberMobileFriendly;

        if (removedConferenceCallRoles.length > 0 && !this.requiresConferenceCall(positions)) {
            conferenceNumber = '';
            conferenceNumberCode = '';
            conferenceNumberMobileFriendly = '';
        }

        let hashData = this.getHashData();
        hashData.positions = positions;

        let dataHash = hash(hashData);

        this.setState({
            positions,
            conferenceNumber,
            conferenceNumberCode,
            conferenceNumberMobileFriendly,
            pendingChanges: this.state.dataHash != dataHash,
            showRemoveOpsModal: false,
            pendingOpsSave: null
        });
    }

    handleTagsHaveChanged = (tags: ITag[], tagSuggestions: ITag[]) => {
        this.setState({
            tags,
            tagSuggestions,
            pendingChanges: true
        });
    }

    handleSave = () => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            let { street, city, state, postalCode } = validation.primaryAddressOverride;
            let primaryAddress = this.state.entityType == ContactEntityType.Center ? validation.primaryAddress : street && city && state && postalCode;

            let valid =
                validation.entityType
                && validation.positions
                && validation.firstName
                && validation.lastName
                && validation.email
                && primaryAddress
                && validation.phoneNumbers
                && (this.requiresConferenceCall(this.state.positions) ? validation.conferenceNumber && validation.conferenceNumberCode && validation.conferenceNumberMobileFriendly : true)

            if (valid) {
                let { 
                    id, organType, entityType, entityTypeId, title, prefix, firstName, middleName, lastName, suffix, email, secondaryEmail, emailSignature,
                    primaryAddress: addressId1, primarySuite: address1Suite, primaryAddressOverride: address1Override,
                    secondaryAddress: addressId2, secondarySuite: address2Suite, secondaryAddressOverride: address2Override,
                    emailOverride: galaEmailOverride, conferenceNumber, conferenceNumberCode, conferenceNumberMobileFriendly,
                    positions, phoneNumbers, emailFlags: allEmailFlags, tags
                } = this.state;

                let emailFlags = allEmailFlags.filter(x => x.value == 1);

                let request: IContactRequest = {
                    id,
                    isDeleted: false,
                    entityType,
                    entityTypeId,
                    title: (title || '').trim(),
                    prefix: (prefix || '').trim(),
                    firstName: (firstName || '').trim(),
                    middleName: (middleName || '').trim(),
                    lastName: (lastName || '').trim(),
                    nameSuffix: (suffix || '').trim(),
                    email: (email || '').trim(),
                    secondaryEmail: (secondaryEmail || '').trim(),
                    emailSignature,
                    addressId1,
                    address1Suite,
                    address1Override,
                    addressId2,
                    address2Suite,
                    address2Override,
                    galaEmailOverride,
                    conferenceNumber: conferenceNumber || '',
                    conferenceNumberCode: conferenceNumberCode || '',
                    conferenceNumberMobileFriendly: conferenceNumberMobileFriendly || '',
                    organType,
                    positions,
                    emailFlags,
                    phoneNumbers: phoneNumbers.map(ph => {
                        if (ph.id < 0) {
                            ph.id = 0;
                        }

                        return ph;
                    }),
                    tags: tags.map(t => t.name)
                };

                this.props.showLoading('Saving contact, one moment please...').then(() => {
                    this.props.saveContact(request)
                        .then(response => {
                            if (response.data.success && request.id == 0) {
                                let { id, entityType, entityTypeId, firstName, middleName, lastName, positionText: title,
                                    nameHonorific: prefix, nameSuffix: suffix,
                                    email, secondaryEmail, emailSignature,
                                    addressId1: primaryAddress, address1Suite: primarySuite, address1Override: primaryAddressOverride,
                                    addressId2: secondaryAddress, address2Suite: secondarySuite, address2Override: secondaryAddressOverride,
                                    positions, emailFlags, phoneNumbers, center } = response.data.results.contact;

                                this.setState({
                                    id,
                                    entityType,
                                    entityTypeId,
                                    firstName, middleName, lastName,
                                    title,
                                    prefix,
                                    suffix,
                                    email,
                                    secondaryEmail,
                                    emailSignature,
                                    primaryAddress,
                                    primarySuite,
                                    primaryAddressOverride,
                                    secondaryAddress,
                                    secondarySuite,
                                    secondaryAddressOverride,
                                    positions: positions.map(p => p.id),
                                    savedPositions: positions.map(p => p.id),
                                    emailFlags,
                                    phoneNumbers: phoneNumbers.map(x => ({ ...x, edit: false })),
                                    center,
                                    save: {
                                        status: 1,
                                        message: ''
                                    }
                                });
                            }

                            if (response.data.success && request.id > 0) {
                                let d = this.getHashData();

                                let dataHash = this.calcHash(d.entityType, d.entityTypeId, d.title, d.prefix, d.firstName, d.middleName, d.lastName, d.suffix, d.email, d.secondaryEmail,
                                    d.primaryAddress, d.primarySuite, d.primaryAddressOverride, d.secondaryAddress, d.secondarySuite, d.secondaryAddressOverride,
                                    d.positions, d.phoneNumbers, d.emailFlags);

                                this.setState({
                                    dataHash,
                                    pendingChanges: false,
                                    save: {
                                        status: 1,
                                        message: ''
                                    }
                                }, () => {
                                    this.getData();
                                });
                            }

                            if (!response.data.success) {
                                this.setState({
                                    save: {
                                        status: 2,
                                        message: response.data.message
                                    }
                                });
                            } else {
                                if (request.id == 0) {
                                    let d = this.getHashData();

                                    let dataHash = this.calcHash(d.entityType, d.entityTypeId, d.title, d.prefix, d.firstName, d.middleName, d.lastName, d.suffix, d.email, d.secondaryEmail,
                                        d.primaryAddress, d.primarySuite, d.primaryAddressOverride, d.secondaryAddress, d.secondarySuite, d.secondaryAddressOverride,
                                        d.positions, d.phoneNumbers, d.emailFlags);

                                    this.setState({
                                        id: response.data.results.contact.id,
                                        phoneNumbers: response.data.results.contact.phoneNumbers.map(x => ({ ...x, edit: false })),
                                        dataHash,
                                        pendingChanges: false,
                                        save: {
                                            status: 1,
                                            message: ''
                                        }
                                    }, () => {
                                        let { hostname, protocol, port } = window.location;
                                        let newUrl = `${protocol}//${hostname}:${port}/contact/${response.data.results.contact.id}`;

                                        window.history.pushState({}, '', newUrl);
                                    });
                                }
                            }

                            this.props.hideLoading();

                            window.scrollTo(0, 0);
                        })
                        .catch(error => {
                            this.props.hideLoading();
                        })
                        ;
                });
            } else {
                window.scrollTo(0, 0);
            }
        });
    }

    handleCancel = () => {
        if (this.props.isCenter) {
            window.location.href = `/center/${this.state.entityTypeId}/profile/contacts`;
        } else {
            window.location.href = '/contacts';
        }
    }

    render = () => {
        const centerAddresses = _.orderBy(this.props.contact.lookupData.centerAddresses.filter(x => x.centerId == this.state.entityTypeId && x.isDeleted == false), (item) => { return item.typeId });
        const { entityType, positions, firstName, lastName, email, primaryAddress, primaryAddressOverride, phoneNumbers } = this.state.validation;
        const primaryAddressOverrideValid = primaryAddressOverride.street && primaryAddressOverride.city && primaryAddressOverride.state && primaryAddressOverride.postalCode;
        const primaryAddressValid = this.state.entityType == ContactEntityType.Center ? primaryAddress : primaryAddressOverrideValid;
        const isValid = entityType && positions && firstName && lastName && email && primaryAddressValid && phoneNumbers;

        return (
            <>
                <div className='container' style={{ paddingBottom: 10 }}>
                    {this.state.dirty && !isValid && (
                        <ValidationInfo
                            validation={this.state.validation}
                            entityType={this.state.entityType}
                            positions={this.state.positions}
                        />
                    )}

                    {this.state.save.status == 1 && (
                        <Row>
                            <Col xs={12} lg={12}>
                                <Alert bsStyle='success'>
                                    Contact saved successfully!
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {this.state.save.status == 2 && (
                        <Row>
                            <Col xs={12} lg={12}>
                                <Alert bsStyle='danger'>
                                    <div>Unable to save the contact due to the following:</div>
                                    <div>&middot;{this.state.save.message}</div>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, marginTop: 5 }}>
                        {this.props.contactId == 0 && this.state.id == 0 ?
                            <h1 style={{ marginBottom: 0 }}>{this.props.app.site.organType == OrganType.Liver ? 'Add Liver Contact' : 'Add Kidney Contact'}</h1>
                            :
                            <h1 style={{ marginBottom: 0 }}>Edit Contact</h1>
                        }

                        <div>
                            {this.state.pendingChanges && (
                                <Label bsStyle='warning' style={{ fontSize: '100%' }}>Pending Changes!</Label>
                            )}
                        </div>

                        <div>
                            <Button bsStyle='primary' bsSize='small' disabled={!this.state.pendingChanges} onClick={this.handleSave}>Save Contact</Button>
                            <Button bsStyle='link' bsSize='small' onClick={this.handleCancel}>Cancel &amp; Return to Contact List</Button>
                        </div>
                    </div>

                    <Row>
                        <Col xs={12} lg={6}>
                            <Panel>
                                <Panel.Heading>Contact Info</Panel.Heading>
                                <Panel.Body>
                                    <ContactInfoContent
                                        isCenter={this.props.isCenter}
                                        organType={this.state.organType}
                                        entityType={this.state.entityType}
                                        entityTypeId={this.state.entityTypeId}
                                        positions={this.state.positions}
                                        title={this.state.title}
                                        prefix={this.state.prefix}
                                        firstName={this.state.firstName}
                                        middleName={this.state.middleName}
                                        lastName={this.state.lastName}
                                        suffix={this.state.suffix}
                                        email={this.state.email}
                                        secondaryEmail={this.state.secondaryEmail}
                                        primaryAddress={this.state.primaryAddress}
                                        primarySuite={this.state.primarySuite}
                                        primaryAddressOverride={this.state.primaryAddressOverride}
                                        secondaryAddress={this.state.secondaryAddress}
                                        secondarySuite={this.state.secondarySuite}
                                        secondaryAddressOverride={this.state.secondaryAddressOverride}
                                        validation={this.state.validation}
                                        centerList={this.state.centerList}
                                        courierList={this.props.contact.lookupData.swapCouriers}
                                        contactEntityTypes={this.props.contact.lookupData.contactEntityTypes}
                                        positionList={this.props.contact.lookupData.positions}
                                        centerAddressList={centerAddresses}
                                        onNumberValueChange={this.handleNumberValueChange}
                                        onAddRoles={this.handleAddRoles}
                                        onRemoveRoles={this.handleRemoveRoles}
                                        onTextChange={this.handleTextChange}
                                    />
                                </Panel.Body>
                            </Panel>
                        </Col>
                        <Col xs={12} lg={6}>
                            <PhoneNumbersPanel
                                contactId={this.state.id}
                                phoneNumbers={this.state.phoneNumbers}
                                phoneTypes={this.props.contact.lookupData.phoneTypes}
                                mobileProviders={this.props.contact.lookupData.mobileProviders}
                                showMobileProvider={true}
                                onChange={this.handlePhoneNumbersHaveChanged}
                            />

                            {this.props.app.site.organType == OrganType.Kidney && (
                                <EmailOptions
                                    data={this.state.emailFlags}
                                    emailFlags={this.props.contact.lookupData.emailFlags}
                                    emailFlagGroups={this.props.contact.lookupData.emailFlagGroups}
                                    onChange={this.handleEmailFlagsHaveChanged}
                                />
                            )}

                            {this.props.isCenter == false && (
                                <Gala
                                    tags={this.state.tags}
                                    suggestions={this.state.tagSuggestions}
                                    emailOverride={this.state.emailOverride}
                                    onChange={this.handleTagsHaveChanged}
                                    onEmailOverrideChange={this.handleTextChange}
                                />
                            )}

                            {this.isNetworkEmailSender() && (
                                <NetworkEmailSignature value={this.state.emailSignature} onChange={this.handleTextChange} />
                            )}

                            {this.requiresConferenceCall(this.state.positions) && (
                                <ConferenceCallPanel
                                    conferenceNumber={this.state.conferenceNumber}
                                    conferenceNumberCode={this.state.conferenceNumberCode}
                                    conferenceNumberMobileFriendly={this.state.conferenceNumberMobileFriendly}
                                    validation={this.state.validation}
                                    onChange={this.handleTextChange}
                                />
                            )}

                        </Col>
                    </Row>

                    {this.state.user?.isMicrositeCoach == true && !this.isNetworkEmailSender() && (
                        <Row>
                            <Col xs={12}>
                                <EmailSignature value={this.state.emailSignature} onChange={this.handleTextChange} />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col xs={12} lg={12} className='text-center'>
                            <Button bsStyle='primary' bsSize='lg' disabled={!this.state.pendingChanges} onClick={this.handleSave}>Save Contact</Button>
                            <Button bsStyle='link' onClick={this.handleCancel}>Cancel &amp; Return to Contact List</Button>
                        </Col>
                    </Row>
                </div>

                <Modal show={this.state.showRemoveOpsModal} onHide={this.handleCancelRemoveOps} backdrop='static'>
                    <Modal.Header closeButton>Remove Operations Role</Modal.Header>
                    <Modal.Body>
                        This person is still scheduled for shifts. Removing them from Operations will also remove all their scheduled shifts. Are you sure you want to continue?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.handleCancelRemoveOps}>Cancel</Button>
                        <Button bsStyle='danger' onClick={this.handleConfirmRemoveOps}>Confirm</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}


const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        contact: appState.contact,
        centerList: appState.centers.list,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getContact: (contactId) => {
            return dispatch(ContactActionCreators.getContact(contactId));
        },
        getBasicCenterList: () => {
            return dispatch(CenterActionCreators.getBasicCenterList(false));
        },
        getContactPositionList: () => {
            return dispatch(ContactActionCreators.getContactPositions());
        },
        getContactLookupData: (centerId, organType) => {
            return dispatch(ContactActionCreators.getContactLookupData(centerId, organType));
        },
        saveContact: (request) => {
            return dispatch(ContactActionCreators.saveContact(request));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(AddEdit)